import { useState, useEffect } from 'react';
import { X, Clock, CheckCircle, XCircle, ChevronLeft, ChevronRight, Filter, RefreshCw, Eye } from 'lucide-react';
import type { SavedQuery } from '../../types/query';
import type { ExecutionLog, TriggerType } from '../../types/executionLog';
import { executionLogService } from '../../services/executionLogService';
import ExecutionLogDetailModal from './ExecutionLogDetailModal';

interface ExecutionHistoryModalProps {
  query: SavedQuery;
  onClose: () => void;
}

const ITEMS_PER_PAGE = 20;

export default function ExecutionHistoryModal({ query, onClose }: ExecutionHistoryModalProps) {
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState<TriggerType | ''>('');
  const [selectedLog, setSelectedLog] = useState<ExecutionLog | null>(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await executionLogService.getLogs({
        serviceId: query.id,
        limit: ITEMS_PER_PAGE,
        offset: (page - 1) * ITEMS_PER_PAGE,
        triggerType: filterType || undefined
      });

      setLogs(result.logs);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [query.id, page, filterType]);

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  const handleFilterChange = (type: TriggerType | '') => {
    setFilterType(type);
    setPage(1);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Histórico de Execuções</h2>
              <p className="text-sm text-gray-500 mt-1">{query.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={filterType}
                onChange={(e) => handleFilterChange(e.target.value as TriggerType | '')}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Todos os tipos</option>
                <option value="manual">Manual</option>
                <option value="api">API</option>
                <option value="webhook">Webhook</option>
                <option value="solace">Solace</option>
                <option value="sap_btp">SAP BTP</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                {total} {total === 1 ? 'registro' : 'registros'}
              </span>
              <button
                onClick={fetchLogs}
                disabled={loading}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            {loading && logs.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-12 text-red-600">
                <XCircle className="w-8 h-8 mb-2" />
                <p>{error}</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Clock className="w-8 h-8 mb-2" />
                <p>Nenhuma execução encontrada</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data/Hora
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HTTP
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tempo
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {executionLogService.formatDate(log.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${executionLogService.getTriggerTypeColor(log.trigger_type)}`}>
                          {executionLogService.getTriggerTypeLabel(log.trigger_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {log.user_name || log.user_email ? (
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate" title={log.user_name || undefined}>
                              {log.user_name || 'Usuário sem nome'}
                            </p>
                            {log.user_email && (
                              <p className="text-xs text-gray-500 truncate" title={log.user_email}>
                                {log.user_email}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Não identificado</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {log.success ? (
                          <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            Sucesso
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 text-sm">
                            <XCircle className="w-4 h-4" />
                            Erro
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {log.response_status ? (
                          <span className={`font-mono ${log.response_status >= 200 && log.response_status < 300 ? 'text-green-600' : 'text-red-600'}`}>
                            {log.response_status}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                        {executionLogService.formatExecutionTime(log.execution_time_ms)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => setSelectedLog(log)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Página {page} de {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedLog && (
        <ExecutionLogDetailModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}
    </>
  );
}
